import { useState, useEffect, useCallback } from 'react';
import { Heart, Zap, Copy, Check, ExternalLink, Sparkle, Sparkles, Star, Rocket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/useToast';
import QRCode from 'qrcode';
import type { WebLNProvider } from '@webbtc/webln-types';

const LIGHTNING_ADDRESS = 'cashucards@getalby.com';

const presetAmounts = [
  { amount: 21, icon: Sparkle },
  { amount: 100, icon: Sparkles },
  { amount: 500, icon: Zap },
  { amount: 1000, icon: Star },
  { amount: 5000, icon: Rocket },
];

/** Resolve a Lightning Address to a LNURL-pay endpoint and fetch its metadata. */
async function resolveLightningAddress(address: string): Promise<{ callback: string; minSendable: number; maxSendable: number } | null> {
  const [name, domain] = address.split('@');
  if (!name || !domain) return null;

  try {
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${encodeURIComponent(`https://${domain}/.well-known/lnurlp/${name}`)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.tag !== 'payRequest' || !data.callback) return null;
    return {
      callback: data.callback,
      minSendable: data.minSendable ?? 1000,
      maxSendable: data.maxSendable ?? 100000000000,
    };
  } catch {
    return null;
  }
}

/** Request an invoice from the LNURL-pay callback. */
async function fetchInvoice(callback: string, amountMsat: number, comment?: string): Promise<string | null> {
  try {
    let url = `${callback}${callback.includes('?') ? '&' : '?'}amount=${amountMsat}`;
    if (comment) url += `&comment=${encodeURIComponent(comment)}`;
    const res = await fetch(`https://proxy.shakespeare.diy/?url=${encodeURIComponent(url)}`);
    if (!res.ok) return null;
    const data = await res.json();
    return data.pr ?? null;
  } catch {
    return null;
  }
}

export function Value4Value() {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState<number | string>(100);
  const [invoice, setInvoice] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [paid, setPaid] = useState(false);
  const { toast } = useToast();

  const webln = (globalThis as { webln?: WebLNProvider }).webln ?? null;

  // Generate QR code when invoice changes
  useEffect(() => {
    if (!invoice) { setQrCodeUrl(''); return; }
    let cancelled = false;
    QRCode.toDataURL(invoice.toUpperCase(), { width: 512, margin: 2, color: { dark: '#000000', light: '#FFFFFF' } })
      .then((url) => { if (!cancelled) setQrCodeUrl(url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [invoice]);

  // Reset on open/close
  useEffect(() => {
    if (open) {
      setAmount(100);
      setInvoice(null);
      setQrCodeUrl('');
      setCopied(false);
      setPaid(false);
    }
  }, [open]);

  const handlePay = useCallback(async () => {
    const sats = typeof amount === 'string' ? parseInt(amount, 10) : amount;
    if (!sats || sats <= 0) {
      toast({ title: 'Invalid amount', description: 'Please enter a valid amount in sats.', variant: 'destructive' });
      return;
    }

    setIsLoading(true);
    try {
      const lnurl = await resolveLightningAddress(LIGHTNING_ADDRESS);
      if (!lnurl) throw new Error('Could not resolve Lightning Address.');

      const msat = sats * 1000;
      if (msat < lnurl.minSendable || msat > lnurl.maxSendable) {
        throw new Error(`Amount must be between ${Math.ceil(lnurl.minSendable / 1000)} and ${Math.floor(lnurl.maxSendable / 1000)} sats.`);
      }

      const inv = await fetchInvoice(lnurl.callback, msat, 'Value4Value from CashuCards!');
      if (!inv) throw new Error('Could not get a Lightning invoice.');

      // Try WebLN auto-pay first
      if (webln) {
        try {
          if (webln.enable) await webln.enable();
          await webln.sendPayment(inv);
          setPaid(true);
          toast({ title: 'Payment sent!', description: `You sent ${sats} sats. Thank you!` });
          setIsLoading(false);
          return;
        } catch {
          // WebLN failed, fall through to show QR
        }
      }

      setInvoice(inv);
    } catch (err) {
      toast({ title: 'Error', description: (err as Error).message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  }, [amount, webln, toast]);

  const handleCopy = useCallback(async () => {
    if (!invoice) return;
    try {
      await navigator.clipboard.writeText(invoice);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = invoice;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast({ title: 'Copied!', description: 'Lightning invoice copied to clipboard.' });
    setTimeout(() => setCopied(false), 2000);
  }, [invoice, toast]);

  const openInWallet = useCallback(() => {
    if (invoice) window.open(`lightning:${invoice}`, '_blank');
  }, [invoice]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 border-rose-300 dark:border-rose-700 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:border-rose-400 dark:hover:border-rose-600 transition-all"
        >
          <Heart className="w-4 h-4" />
          Value 4 Value
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[420px] max-h-[95vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="text-lg">
            {paid ? 'Thank You!' : invoice ? 'Pay Invoice' : 'Send Value 4 Value'}
          </DialogTitle>
          <DialogDescription className="text-sm text-center">
            {paid
              ? 'Your support means the world to us!'
              : invoice
                ? 'Scan the QR code or copy the invoice to pay with any Lightning wallet.'
                : `Support CashuCards by sending sats to ${LIGHTNING_ADDRESS}`}
          </DialogDescription>
        </DialogHeader>

        {paid ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Your {typeof amount === 'string' ? amount : amount.toLocaleString()} sats have been sent. Thank you for supporting CashuCards!
            </p>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          </div>
        ) : invoice ? (
          <div className="space-y-4">
            <div className="text-center">
              <span className="text-2xl font-bold">{typeof amount === 'string' ? amount : amount.toLocaleString()} sats</span>
            </div>
            <Separator />
            <div className="flex justify-center">
              <Card className="p-3 max-w-[280px] mx-auto">
                <CardContent className="p-0 flex justify-center">
                  {qrCodeUrl ? (
                    <img src={qrCodeUrl} alt="Lightning Invoice QR Code" className="w-full h-auto aspect-square object-contain" />
                  ) : (
                    <div className="w-full aspect-square bg-muted animate-pulse rounded" />
                  )}
                </CardContent>
              </Card>
            </div>
            <div className="space-y-2">
              <Label>Lightning Invoice</Label>
              <div className="flex gap-2">
                <Input value={invoice} readOnly className="font-mono text-xs flex-1 overflow-hidden text-ellipsis" onClick={(e) => e.currentTarget.select()} />
                <Button variant="outline" size="icon" onClick={handleCopy} className="shrink-0">
                  {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            {webln && (
              <Button onClick={handlePay} disabled={isLoading} className="w-full">
                <Zap className="w-4 h-4 mr-2" />
                {isLoading ? 'Processing...' : 'Pay with WebLN'}
              </Button>
            )}
            <Button variant="outline" onClick={openInWallet} className="w-full">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open in Lightning Wallet
            </Button>
            <Button variant="ghost" onClick={() => { setInvoice(null); setQrCodeUrl(''); }} className="w-full text-xs">
              Back to amount selection
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <ToggleGroup
              type="single"
              value={String(amount)}
              onValueChange={(v) => { if (v) setAmount(parseInt(v, 10)); }}
              className="grid grid-cols-5 gap-1 w-full"
            >
              {presetAmounts.map(({ amount: a, icon: Icon }) => (
                <ToggleGroupItem key={a} value={String(a)} className="flex flex-col h-auto min-w-0 text-xs px-1 py-2">
                  <Icon className="h-4 w-4 mb-1" />
                  <span className="truncate">{a.toLocaleString()}</span>
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <div className="flex items-center gap-2">
              <div className="h-px flex-1 bg-muted" />
              <span className="text-xs text-muted-foreground">OR</span>
              <div className="h-px flex-1 bg-muted" />
            </div>
            <Input
              type="number"
              placeholder="Custom amount in sats"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full"
            />
            <Button onClick={handlePay} disabled={isLoading} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/20">
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full" />
                  Getting invoice...
                </span>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Send {typeof amount === 'string' ? amount : amount.toLocaleString()} sats
                </>
              )}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
